import SFShuffleIcon from '@/component/icons/symbol/sf-shuffle-icon';
import SFTriceIcon from '@/component/icons/symbol/sf-trice-icon';

import EthIcon from '../icons/symbol/eth-icon';
import PolygonIcon from '../icons/symbol/polygon-icon';
import BnbIcon from '../icons/symbol/bnb-icon';
import TetherIcon from '../icons/symbol/tether';

import sflErc20Abi from '@/lib/web3/abi/SflErc20abi.json';
import polygonErc20Abi from '@/lib/web3/abi/Erc20PolygonSflTrlabi.json';

import {
  COIN_NAME_SFL,
  COIN_NAME_TRI,
  TRI_CONTRACT,
  POLYGON_USDT,
  POLYGON_EDOGE,
  POLYGON_SHUFFLE,
  POLYGON_MAIN,
  POLYGON_AMOY,
  BNB_SMART_CHAIN,
  ETHER_MAIN,
  POLYGON_TRI,
  TEST_SEPOLIA,
} from '@/lib/common/name-space/';

// 보안을 위한 체인 ID 검증 함수
const isValidChainId = (chainId: number): boolean => {
  const validChainIds = [22023, ETHER_MAIN, POLYGON_MAIN, POLYGON_AMOY, BNB_SMART_CHAIN, TEST_SEPOLIA];
  return validChainIds.includes(chainId);
};

// 지원하는 체인 목록 (결제 요청 등에서 사용)
export const SUPPORTED_CHAINS = [
  { id: POLYGON_MAIN, name: 'Polygon', displayName: '폴리곤' },
  // { id: ETHER_MAIN, name: 'Ethereum', displayName: '이더리움' },
  // { id: BNB_SMART_CHAIN, name: 'BNB Smart Chain', displayName: 'BNB' },
  // { id: 22023, name: 'Taycan', displayName: '타이칸' },
];

export type resNetworkList = {
  name: string;
  symbol: string;
  address: string;
  token: string | undefined;
  abi?: any[];
  icon: JSX.Element;
};

// ※ 체인을 추가 삭제 할 때마다 Chain.ts에서 네트워크관련 수정 필요
export default async function networkList(aChainId: number = POLYGON_MAIN): Promise<resNetworkList[]> {
  try {
    // 체인 ID 검증
    if (!isValidChainId(aChainId)) {
      console.warn(`유효하지 않은 체인 ID: ${aChainId}, 기본값 사용`);
      aChainId = POLYGON_MAIN;
    }

    let re: resNetworkList[] = [];

    switch (aChainId) {
      case 22023: // taycan
        re = await taycanList();
        break;
      case ETHER_MAIN: // eth
        re = await ethList();
        break;
      case POLYGON_MAIN: // polygon
        re = await polygonList();
        break;
      case POLYGON_AMOY:
        re = await polygonTestList();
        break;
      case BNB_SMART_CHAIN: // BNB Smart Chain
        re = await bnbSmartChain();
        break;
      case TEST_SEPOLIA:
        re = await ethTestList();
        break;
      default:
        re = await polygonList();
        break;
    }

    return re;
  } catch (error) {
    console.error('네트워크 리스트 로딩 중 오류 발생:', error);
    // 오류 발생 시 기본값 반환
    return await polygonTestList();
  }
}

const taycanList = async (): Promise<resNetworkList[]> => [
  {
    name: '셔플',
    symbol: COIN_NAME_SFL,
    address: '',
    token: '',
    icon: <SFShuffleIcon />,
  },
  {
    name: '트라이스',
    symbol: COIN_NAME_TRI,
    address: '',
    token: TRI_CONTRACT,
    icon: <SFTriceIcon />,
  },
];

const ethList = async (): Promise<resNetworkList[]> => [
  {
    name: '이더',
    symbol: 'ETH',
    address: '',
    token: '',
    icon: <EthIcon />,
  },
];

const polygonList = async (): Promise<resNetworkList[]> => [
  {
    name: '폴리곤',
    symbol: 'POL',
    address: '',
    token: undefined,
    icon: <PolygonIcon />,
  },
  // {
  //   name: '테더',
  //   symbol: 'USDT',
  //   address: '',
  //   token: POLYGON_USDT,
  //   icon: <TetherIcon />,
  // },
  {
    name: '셔플',
    symbol: COIN_NAME_SFL,
    address: '',
    token: POLYGON_SHUFFLE,
    abi: polygonErc20Abi,
    icon: <SFShuffleIcon />,
  },
  {
    name: '트라이스',
    symbol: COIN_NAME_TRI,
    address: '',
    token: POLYGON_TRI,
    abi: polygonErc20Abi,
    icon: <SFTriceIcon />,
  },

  // {
  //   name: '도지',
  //   symbol: 'EDOGE',
  //   address: '',
  //   token: POLYGON_EDOGE,
  //   icon: <TetherIcon />,
  // },
];

const polygonTestList = async (): Promise<resNetworkList[]> => [
  {
    name: '폴리곤',
    symbol: 'POL',
    address: '',
    token: undefined,
    icon: <PolygonIcon />,
  },
  // {
  //   name: '테더',
  //   symbol: 'USDT',
  //   address: '',
  //   token: '0x41E94Eb019C0762f9Bfcf9Fb1E58725BfB0e7582',
  //   icon: <TetherIcon />,
  // },
  {
    name: '셔플',
    symbol: COIN_NAME_SFL,
    address: '',
    token: POLYGON_SHUFFLE,
    abi: sflErc20Abi,
    icon: <SFShuffleIcon />,
  },
  {
    name: '트라이스',
    symbol: COIN_NAME_TRI,
    address: '',
    token: POLYGON_TRI,
    abi: sflErc20Abi,
    icon: <SFTriceIcon />,
  },
];

const bnbSmartChain = async (): Promise<resNetworkList[]> => [
  {
    name: 'Bnb',
    symbol: 'BNB',
    address: '',
    token: undefined,
    icon: <BnbIcon />,
  },
];

const klaytnList = async (): Promise<resNetworkList[]> => [
  {
    name: 'Klaytn',
    symbol: 'KLAY',
    address: '',
    token: undefined,
    icon: <SFShuffleIcon />,
  },
];

const ethTestList = async (): Promise<resNetworkList[]> => [
  {
    name: '이더',
    symbol: 'ETH',
    address: '',
    token: '',
    icon: <EthIcon />,
  },
];
